import type { PiralInstance } from 'piral-core';

import { APP_SHELL_IDENTIFIER, type InternalTrackingObject } from '../types';

const STORAGE_KEY = 'mayer:login-time-start';
const MAX_AGE_MS = 5 * 60 * 1000;
const APP_READY_TIMEOUT_MS = 60_000;

/**
 * Called right before redirect to ZEISS ID.
 *
 * Writes a marker to sessionStorage so that on the second page load (after returning
 * from ZEISS ID) the app knows this is a fresh login flow and can record a precise
 * start time via {@link updateLoginStartOnSessionConfirmed}.
 */
export function markLoginRedirectStart(): void {
  sessionStorage.setItem(STORAGE_KEY, Date.now().toString());
}

/**
 * Called after `fetchAndStoreSessionData()` returns a valid session on the second page load.
 *
 * If a login marker exists in sessionStorage (placed by {@link markLoginRedirectStart}
 * on the previous page load), overwrites it with the current client-side `Date.now()`.
 *
 * This means `spaOnlyLoadDurationMs` will measure from the moment the frontend confirms
 * that the user is authenticated — **after** the ZEISS ID round-trip, and therefore
 * excluding the time the user spent typing credentials.
 *
 * Does nothing when called during a regular page refresh (no marker in storage).
 */
export function updateLoginStartOnSessionConfirmed(): void {
  if (sessionStorage.getItem(STORAGE_KEY) === null) {
    return;
  }

  sessionStorage.setItem(STORAGE_KEY, Date.now().toString());
}

/**
 * Waits until the Piral app shell has finished loading all pilets
 * (`app.loading` becomes `false`) and the browser has painted the result.
 *
 * Resolves after a double-requestAnimationFrame (rAF) so that the Welcome Page
 * content (not the skeleton) is actually visible to the user.
 *
 * Rejects after {@link APP_READY_TIMEOUT_MS} to avoid waiting forever.
 */
export function waitForAppShellReady(instance: PiralInstance): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      unsubscribe?.();
      reject(new Error('waitForAppShellReady: timed out'));
    }, APP_READY_TIMEOUT_MS);

    const onReady = () => {
      window.clearTimeout(timeout);
      // Double-rAF (requestAnimationFrame): first rAF fires before the next paint, second one fires
      // after the browser has actually painted the rendered content.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          resolve();
        }),
      );
    };

    // If pilets are already loaded, resolve immediately (after paint).
    const isLoading = instance.context.readState((s) => s.app.loading);
    if (!isLoading) {
      onReady();
      return;
    }

    // Subscribe to zustand store changes and wait for loading to finish.
    const unsubscribe = instance.context.state.subscribe((state) => {
      if (!state.app.loading) {
        unsubscribe();
        onReady();
      }
    });
  });
}

/**
 * Push a single performance event to dataLayer. Returns true if event was pushed.
 *
 * Timeline of the two measured durations (both measured on the page load AFTER the ZEISS ID redirect):
 *
 * ```
 *  ── page load after ZEISS ID redirect ──────────────────────────────────────────► time
 *
 *  navigationStart          session confirmed              Welcome Page painted
 *  (performance.timeOrigin) (updateLoginStart...)          (double-rAF → event push)
 *       │                         │                                  │
 *       ▼                         ▼                                  ▼
 * ──────●─────────────────────────●──────────────────────────────────●──────────
 *       │                                                            │
 *       │◄────────────────── fullPageLoadDurationMs ─────────────────│
 *       │  HTML fetch, JS parse, SPA init, session check, pilets...  │
 *       │                                                            │
 *                                │◄────── spaOnlyLoadDurationMs ─────│
 *                                │ pilet load + double-rAF paint only│
 * ```
 *
 * Both measurements **end** at the same point (Welcome Page painted).
 * The difference `fullPageLoadDurationMs − spaOnlyLoadDurationMs` represents the SPA bootstrap
 * time before the session was confirmed (HTML fetch, JS parse, session API call, etc.).
 */
export function flushLoginWelcomePagePerformanceEvent(): boolean {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return false;
  }

  const redirectedAt = parseInt(raw, 10);
  const now = Date.now();
  const spaOnlyLoadDurationMs = now - redirectedAt;
  sessionStorage.removeItem(STORAGE_KEY);

  // Guard against stale markers: if the user left the tab idle for too long after
  // returning from ZEISS ID, the elapsed
  // time would be dominated by that idle gap rather than the actual login flow.
  // Discard the measurement to avoid polluting the metric with outlier values.
  if (now - redirectedAt > MAX_AGE_MS) {
    return false;
  }

  // Elapsed ms from session-confirmed moment to Welcome Page paint (SPA-only cost).
  window.dataLayer.push({
    event: 'event',
    eventName: 'Time',
    eventAction: 'Duration',
    eventType: 'Duration - Milliseconds',
    eventValue: spaOnlyLoadDurationMs,
    eventDetail: 'MATRIX - spaOnlyLoadDurationMs',
    contentHierarchy1: APP_SHELL_IDENTIFIER,
    contentHierarchy2: 'Welcome Page',
  } as InternalTrackingObject & { eventValue: number; contentHierarchy2: string });

  // Elapsed ms from browser navigationStart (performance.timeOrigin) to Welcome Page paint (full page-load cost).
  window.dataLayer.push({
    event: 'event',
    eventName: 'Time',
    eventAction: 'Duration',
    eventType: 'Duration - Milliseconds',
    eventValue: Math.round(performance.now()),
    eventDetail: 'MATRIX - fullPageLoadDurationMs',
    contentHierarchy1: APP_SHELL_IDENTIFIER,
    contentHierarchy2: 'Welcome Page',
  } as InternalTrackingObject & { eventValue: number; contentHierarchy2: string });

  return true;
}
